import { useCallback } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CodeMirrorComponent } from "~/components/common/code-mirror";

interface JsonDisplayProps {
  value: string | undefined;
  hideCopy?: boolean;
  useCodeMirrorDisplay?: boolean;
}

function syntaxHighlight(json: string): string {
  const escaped = json
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return escaped.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
    (match) => {
      let cls = "json-number";
      if (/^"/.test(match)) {
        cls = /:$/.test(match) ? "json-key" : "json-string";
      } else if (/true|false/.test(match)) {
        cls = "json-boolean";
      } else if (/null/.test(match)) {
        cls = "json-null";
      }
      return `<span class="${cls}">${match}</span>`;
    },
  );
}

function formatJson(value: string | undefined): {
  highlighted: string;
  plain: string;
} {
  if (!value) return { highlighted: "", plain: "" };
  try {
    const plain = JSON.stringify(JSON.parse(value), undefined, 2);
    return { highlighted: syntaxHighlight(plain), plain };
  } catch {
    return { highlighted: value, plain: value };
  }
}

export function JsonDisplay({
  value,
  hideCopy = false,
  useCodeMirrorDisplay = false,
}: JsonDisplayProps) {
  const { t } = useTranslation();
  const { highlighted, plain } = formatJson(value);

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(plain);
      toast.success(t("common.copySuccess", { defaultValue: "Copied!" }));
    } catch (e) {
      console.error("Could not copy to clipboard", e);
      toast.error(t("common.copyError", { defaultValue: "Failed to copy" }));
    }
  }, [plain, t]);

  if (!value) return null;

  return (
    <div className="relative overflow-x-auto text-left">
      {!useCodeMirrorDisplay && (
        <style>{`
        .json-key    { color: #7c9cff; }
        .json-string { color: #7ec488; }
        .json-number { color: #e5a44e; }
        .json-boolean{ color: #569cd6; }
        .json-null   { color: #e06c75; }
        .dark .json-key    { color: #9db8ff; }
        .dark .json-string { color: #98c99e; }
        .dark .json-number { color: #f0b86e; }
        .dark .json-boolean{ color: #6ab0e8; }
        .dark .json-null   { color: #e8848e; }
      `}</style>
      )}
      {!hideCopy && (
        <button
          type="button"
          onClick={copyToClipboard}
          className="absolute right-2 top-2 flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs text-gray-600 shadow hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <Copy size={12} />
          {t("common.copy")}
        </button>
      )}
      {useCodeMirrorDisplay ? (
        <CodeMirrorComponent value={plain} notEditable height="auto" />
      ) : (
        <pre
          className="text-xs leading-relaxed"
          dangerouslySetInnerHTML={{ __html: highlighted }}
        />
      )}
    </div>
  );
}
